import React from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import PromoCodeInput from "@/components/PromoCodeInput";
import CompBanner from "@/components/CompBanner";
import { toast } from "sonner";
import { isNativeApp, isIOS } from "@/lib/nativeBridge";
import IapConsumableButton from "@/components/IapConsumableButton";
import useIsUsAccount from "@/hooks/useIsUsAccount";

export default function CertificatePurchaseDialog({
  open,
  onClose,
  dogName,
  certPrice,
  promo,
  onPromoApplied,
  onPromoCleared,
  onCheckout,
  purchasing,
}) {
  const isUsAccount = useIsUsAccount();
  const price = certPrice ?? 14.99;
  const handleClose = () => {
    onPromoCleared();
    onClose();
  };
  // App Store 3.1.1 — no in-app purchase UI in the native shell. US
  // accounts get a link-out to the website (US storefront allowance);
  // everyone else gets neutral copy.
  if (isNativeApp()) {
    return (
      <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
        <DialogContent className="max-w-md" data-testid="cert-native-notice">
          <DialogHeader>
            <DialogTitle>Pedigree certificate</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#6B6B6B]">
            A printable PDF certificate for {dogName} — beautifully formatted
            with the full 6-generation pedigree. Certificates are included
            with an active plan.
          </p>
          {isIOS() ? (
            <IapConsumableButton
              creditKey="certificate"
              label="Buy certificate"
              noticeLabel="Certificate purchase"
              onGranted={onCheckout}
            />
          ) : isUsAccount ? (
            <a
              href="https://armstrongk9registry.com/billing"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex w-full items-center justify-center rounded-full bg-ink hover:bg-crimson text-white text-[14px] font-semibold h-11 transition-colors"
              data-testid="cert-native-manage-link"
            >
              Manage your account on armstrongk9registry.com
            </a>
          ) : null}
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={handleClose} data-testid="cert-native-close">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) handleClose();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Unlock pedigree certificate</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-[#6B6B6B]">
            A printable PDF certificate for {dogName} — beautifully formatted with the full
            6-generation pedigree. One-time purchase, downloadable forever, and emailed to you as an
            attachment.
          </p>
          <div className="bg-[#FAFAFA] border border-[#E5E5E5] rounded-lg p-4 flex items-baseline justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">Total</p>
              <p className="font-spectral text-3xl tracking-tight">
                ${promo?.valid ? promo.final.toFixed(2) : price.toFixed(2)}
              </p>
            </div>
            {promo?.valid && (
              <span className="text-sm text-crimson font-semibold">
                −${promo.discount.toFixed(2)}
              </span>
            )}
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mb-1 block">
              Promo code (optional)
            </Label>
            <CompBanner className="mb-3" />
            <PromoCodeInput
              kind="certificate"
              amount={price}
              applied={promo}
              onApplied={(p) => {
                onPromoApplied(p);
                toast.success(`${p.code} applied`);
              }}
              onCleared={onPromoCleared}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} className="rounded-full">
            Cancel
          </Button>
          <Button
            onClick={onCheckout}
            disabled={purchasing}
            className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white"
            data-testid="cert-checkout-btn"
          >
            {purchasing
              ? "Redirecting…"
              : promo?.valid && promo.final <= 0
              ? "Unlock free"
              : `Pay $${promo?.valid ? promo.final.toFixed(2) : price.toFixed(2)} & unlock`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
